import { useState, useEffect } from "react";
import { db } from "../firebase/config";
import {
  collection, doc, getDoc, getDocs, setDoc, addDoc, updateDoc,
  query, where, serverTimestamp, Timestamp, writeBatch
} from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import emailjs from "@emailjs/browser";

const EMAILJS_SERVICE_ID  = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY  = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const FLOOR_ORDER = ["Ground", "1st Floor", "2nd Floor", "3rd Floor"];
// Fallback only — used if a flat is somehow missing its own sortOrder
// field in Firestore. The real, live ordering comes from each flat
// document's own "sortOrder" number (see sortFlats below).
const DEFAULT_FLAT_ORDER = ["G1","G2","G3","101","102","103","201","202","203","301","302","303"];
const RELATIONS   = ["Spouse","Son","Daughter","Parent","Sibling","Relative","Other"];

function sortFlats(flats) {
  return [...flats].sort((a, b) => {
    if (typeof a.sortOrder === "number" && typeof b.sortOrder === "number") {
      return a.sortOrder - b.sortOrder;
    }
    const ai = DEFAULT_FLAT_ORDER.indexOf(a.flatNo);
    const bi = DEFAULT_FLAT_ORDER.indexOf(b.flatNo);
    if (ai !== -1 && bi !== -1) return ai - bi;
    return (a.flatNo || "").localeCompare(b.flatNo || "");
  });
}

function maskAadhar(aadhar) {
  if (!aadhar || aadhar.length < 4) return "—";
  return `XXXX XXXX ${aadhar.slice(-4)}`;
}

function emptyPerson() {
  return { name: "", mobile: "", email: "", aadhar: "", relation: "", dob: "" };
}

// Checks a date string is in dd-mm-yyyy format and is a real calendar date.
// Birthday is optional, so this is only called when something was actually typed in.
function isValidDob(value) {
  const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value.trim());
  if (!match) return false;
  const day = +match[1], month = +match[2], year = +match[3];
  if (month < 1 || month > 12) return false;
  const daysInMonth = new Date(year, month, 0).getDate();
  if (day < 1 || day > daysInMonth) return false;
  return true;
}

// Works out a flat's correct monthly maintenance rate from Settings,
// based on whether the flat is Occupied or Vacant.
// This is the ONE place this calculation happens — every part of the
// screen should use this instead of reading the old maintenanceRate field.
function computeMonthlyRate(flat, settings) {
  if (!settings) return 0;
  const base = Number(settings.maintenanceAmt || 0);
  if (flat?.status === "Vacant") {
    const pct = Number(settings.vacantPct || 0);
    return Math.round(base * (pct / 100));
  }
  return base;
}

// Checks if a flat number belongs to this login.
// Works whether the login has one flat (e.g. "101")
// or a list of flats (e.g. ["101", "G2"]) — like Sunil's dual login.
function userHasFlat(user, flatNo) {
  if (!user?.flatId) return false;
  if (Array.isArray(user.flatId)) return user.flatId.includes(flatNo);
  return user.flatId === flatNo;
}

// ── Status badge ───────────────────────────────────────────────
function StatusBadge({ status }) {
  const occupied = status === "Occupied";
  return (
    <span style={{
      fontSize: "11px", fontWeight: "600", borderRadius: "6px", padding: "2px 8px",
      background: occupied ? "#4caf5018" : "#ffa72618",
      border: `1px solid ${occupied ? "#4caf5040" : "#ffa72640"}`,
      color: occupied ? "#4caf50" : "#ffa726",
    }}>{status}</span>
  );
}

// ── Flat Card ─────────────────────────────────────────────────
function FlatCard({ flat, settings, onClick, showTenantBtn, hasTenant, onTenantClick }) {
  return (
    <div style={{
      background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "14px",
      padding: "14px 16px", marginBottom: "10px",
      transition: "border-color 0.15s",
    }}
      onMouseEnter={e => e.currentTarget.style.borderColor = "#6c63ff60"}
      onMouseLeave={e => e.currentTarget.style.borderColor = "#2e2e40"}
    >
      <div onClick={onClick} style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        cursor: "pointer",
      }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
            <span style={{ fontSize: "14px", fontWeight: "700", color: "#e8e8f0" }}>Flat {flat.flatNo}</span>
            <StatusBadge status={flat.status} />
          </div>
          <div style={{ fontSize: "12px", color: "#8888a8" }}>{flat.ownerName || "—"}</div>
          <div style={{ fontSize: "11px", color: "#555570", marginTop: "2px" }}>
            {flat.floor} · Block {flat.block || "—"} · {flat.doorNo || "—"}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "15px", fontWeight: "700", color: "#6c63ff" }}>
            ₹{computeMonthlyRate(flat, settings).toLocaleString("en-IN")}
          </div>
          <div style={{ fontSize: "10px", color: "#555570", marginTop: "2px" }}>/ month</div>
        </div>
      </div>

      {/* Add Tenant / View Tenant button — own flat only */}
      {showTenantBtn && (
        <div style={{ marginTop: "10px", borderTop: "1px solid #2e2e3060", paddingTop: "10px" }}>
          <button
            onClick={e => { e.stopPropagation(); onTenantClick(); }}
            style={{
              width: "100%", padding: "9px", borderRadius: "8px", fontSize: "12px",
              fontWeight: "600", cursor: "pointer", border: "1px solid",
              background: hasTenant ? "#06b6d415" : "#6c63ff15",
              borderColor: hasTenant ? "#06b6d440" : "#6c63ff40",
              color: hasTenant ? "#06b6d4" : "#6c63ff",
            }}
          >
            {hasTenant ? "👤 View Tenant" : "➕ Add Tenant"}
          </button>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MANAGE TENANT PANEL
// ══════════════════════════════════════════════════════════════
function ManageTenant({ flat, currentUser, onClose }) {
  const [view, setView]       = useState("loading"); // loading | none | form | existing
  const [tenant, setTenant]   = useState(null);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState("");
  const [success, setSuccess] = useState("");
  const [exiting, setExiting] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [exitDateInput, setExitDateInput] = useState(new Date().toISOString().split("T")[0]);

  // persons array — min 2
  const [persons, setPersons]       = useState([emptyPerson(), emptyPerson()]);
  const [primaryIdx, setPrimaryIdx] = useState(0);
  const [paysMaint, setPaysMaint]   = useState(false);
  const [paysWater, setPaysWater]   = useState(false);
  const [entryDate, setEntryDate]   = useState("");

  useEffect(() => { loadTenant(); }, []);

  async function loadTenant() {
    try {
      const q = query(
        collection(db, "users"),
        where("societyId", "==", currentUser?.societyId),
        where("flatId", "==", flat.flatNo),
        where("memberType", "==", "tenant"),
        where("active", "==", true)
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        setTenant({ uid: snap.docs[0].id, ...snap.docs[0].data() });
        setView("existing");
      } else {
        setView("none");
      }
    } catch { setView("none"); }
  }

  function updatePerson(idx, key, value) {
    setError("");
    setPersons(prev => prev.map((p, i) => i === idx ? { ...p, [key]: value } : p));
  }

  function addPerson() {
    setPersons(prev => [...prev, emptyPerson()]);
  }

  function removePerson(idx) {
    if (persons.length <= 2) return;
    setPersons(prev => prev.filter((_, i) => i !== idx));
    if (primaryIdx === idx) setPrimaryIdx(0);
    else if (primaryIdx > idx) setPrimaryIdx(primaryIdx - 1);
  }

  // Validate all persons
  function validatePersons() {
    for (let i = 0; i < persons.length; i++) {
      const p = persons[i];
      if (!p.name.trim())   { setError(`Person ${i+1}: Name is required.`); return false; }
      if (!/^\d{10}$/.test(p.mobile.trim())) { setError(`Person ${i+1}: Valid 10-digit mobile required.`); return false; }
      if (!p.email.trim())  { setError(`Person ${i+1}: Email is required.`); return false; }
      if (!p.aadhar || p.aadhar.replace(/\s/g,"").length !== 12) { setError(`Person ${i+1}: Valid 12-digit Aadhar required.`); return false; }
      if (i > 0 && !p.relation) { setError(`Person ${i+1}: Relation is required.`); return false; }
    }
    if (!entryDate) { setError("Entry date is required."); return false; }
    return true;
  }

  async function handleAddTenant() {
    if (!validatePersons()) return;
    setSaving(true);
    setError("");

    try {
      const primary = persons[primaryIdx];
      const members = persons.filter((_, i) => i !== primaryIdx);

      // Save primary tenant to users collection
      const tenantData = {
        name:            primary.name.trim(),
        mobile:          primary.mobile.trim(),
        emailId:         primary.email.trim(),
        aadhar:          primary.aadhar.replace(/\s/g, ""),
        flatId:          flat.flatNo,
        memberType:      "tenant",
        role:            "Tenant",
        active:          true,
        entryDate:       entryDate,
        exitDate:        null,
        paysMaintenance: paysMaint,
        paysWater:       paysWater,
        upiIds:          [],
        vehicleNos:      [],
        familyMembers:   members.map(m => ({
          name:     m.name.trim(),
          mobile:   m.mobile.trim(),
          email:    m.email.trim(),
          aadhar:   m.aadhar.replace(/\s/g,""),
          relation: m.relation,
        })),
        password:        "RESET_REQUIRED",
        createdAt:       serverTimestamp(),
        createdBy:       currentUser.name,
        failedAttempts:  0,
        lockedUntil:     null,
      };

      const tenantRef = await addDoc(collection(db, "users"), tenantData);

      // Generate reset token and send welcome email
      const tokenArray = new Uint8Array(16);
      window.crypto.getRandomValues(tokenArray);
      const token = Array.from(tokenArray).map(b => b.toString(16).padStart(2,"0")).join("");
      const expiry = new Date(Date.now() + 60 * 60 * 1000);

      await updateDoc(doc(db, "users", tenantRef.id), {
        resetToken:       token,
        resetTokenExpiry: Timestamp.fromDate(expiry),
      });

      // Send welcome email with reset link
      try {
        const resetLink = `${window.location.origin}/reset-password?token=${token}`;
        await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          {
            to_email: primary.email.trim(),
            to_name:  primary.name.trim(),
            link:     resetLink,
          },
          { publicKey: EMAILJS_PUBLIC_KEY }
        );
      } catch (emailErr) {
        console.warn("Welcome email failed:", emailErr);
      }

      // Notify MC
      const mcSnap = await getDocs(query(collection(db, "users"), where("role", "in", ["President","Secretary","Treasurer"])));
      await Promise.all(mcSnap.docs.map(mcDoc =>
        addDoc(collection(db, "notifications"), {
          toUid:     mcDoc.id,
          type:      "tenant_added",
          message:   `New tenant ${primary.name.trim()} added to Flat ${flat.flatNo} by ${currentUser.name}.`,
          isRead:    false,
          createdAt: serverTimestamp(),
        })
      ));

      setSuccess(`✅ Tenant added! Password setup link sent to ${primary.email.trim()}`);
      setTimeout(() => onClose(), 2500);

    } catch (e) {
      console.error(e);
      setError("Failed to add tenant. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  async function handleExitTenant() {
    if (!exitDateInput) { setError("Please enter the exit date."); return; }
    setExiting(true);
    try {
      await updateDoc(doc(db, "users", tenant.uid), {
        active:   false,
        exitDate: exitDateInput,
      });
      const mcSnap = await getDocs(query(collection(db, "users"), where("role", "in", ["President","Secretary","Treasurer"])));
      await Promise.all(mcSnap.docs.map(mcDoc =>
        addDoc(collection(db, "notifications"), {
          toUid:     mcDoc.id,
          type:      "tenant_exited",
          message:   `Tenant ${tenant.name} exited Flat ${flat.flatNo}. Access deactivated.`,
          isRead:    false,
          createdAt: serverTimestamp(),
        })
      ));
      setSuccess("✅ Tenant exit confirmed. Access deactivated.");
      setTimeout(() => onClose(), 2000);
    } catch {
      setError("Failed to process exit. Please try again.");
    } finally {
      setExiting(false);
    }
  }

  // ── Styles ──
  const inputStyle = {
    width: "100%", background: "#0f0f13", border: "1px solid #2e2e40",
    borderRadius: "10px", padding: "11px 14px", color: "#e8e8f0",
    fontSize: "14px", outline: "none", boxSizing: "border-box",
  };
  const labelStyle  = { display: "block", fontSize: "12px", color: "#8888a8", marginBottom: "5px" };
  const fieldStyle  = { marginBottom: "12px" };
  const sectionHdr  = {
    fontSize: "12px", fontWeight: "600", color: "#6c63ff",
    textTransform: "uppercase", letterSpacing: "0.5px",
    marginBottom: "10px", marginTop: "16px",
  };

  if (view === "loading") return (
    <div style={{ textAlign: "center", padding: "30px", color: "#555570", fontSize: "13px" }}>
      Loading tenant info…
    </div>
  );

  return (
    <div>
      <div style={{ borderTop: "1px solid #2e2e40", marginTop: "20px", paddingTop: "20px" }}>
        <div style={{ fontSize: "14px", fontWeight: "700", color: "#e8e8f0", marginBottom: "4px" }}>👤 Tenant</div>
        <div style={{ fontSize: "11px", color: "#555570", marginBottom: "16px" }}>
          {view === "existing" ? "Current tenant details" : "No active tenant — add one below"}
        </div>
      </div>

      {/* ── EXISTING TENANT VIEW ── */}
      {view === "existing" && tenant && (
        <div>
          <div style={{
            background: "#0f0f13", border: "1px solid #2e2e40",
            borderRadius: "12px", padding: "14px 16px", marginBottom: "14px",
          }}>
            <div style={{ fontSize: "14px", fontWeight: "700", color: "#e8e8f0", marginBottom: "10px" }}>
              {tenant.name}
            </div>
            {[
              ["Mobile",     tenant.mobile],
              ["Email",      tenant.emailId || "—"],
              ["Aadhar",     maskAadhar(tenant.aadhar)],
              ["Entry Date", tenant.entryDate || "—"],
              ["Exit Date",  tenant.exitDate  || "Active"],
              ["Vehicle(s)", (tenant.vehicleNos || []).join(", ") || "—"],
              ["UPI ID(s)",  (tenant.upiIds    || []).join(", ") || "—"],
            ].map(([label, value]) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                <span style={{ fontSize: "12px", color: "#555570" }}>{label}</span>
                <span style={{ fontSize: "12px", color: "#8888a8", textAlign: "right", maxWidth: "60%" }}>{value}</span>
              </div>
            ))}
          </div>

          {/* Family members */}
          {(tenant.familyMembers || []).length > 0 && (
            <div style={{
              background: "#0f0f13", border: "1px solid #2e2e40",
              borderRadius: "12px", padding: "14px 16px", marginBottom: "14px",
            }}>
              <div style={{ fontSize: "12px", fontWeight: "600", color: "#8888a8", marginBottom: "10px" }}>
                Family Members
              </div>
              {tenant.familyMembers.map((m, i) => (
                <div key={i} style={{
                  borderBottom: i < tenant.familyMembers.length - 1 ? "1px solid #2e2e40" : "none",
                  paddingBottom: "10px", marginBottom: "10px",
                }}>
                  <div style={{ fontSize: "13px", color: "#e8e8f0", fontWeight: "600" }}>{m.name} <span style={{ fontSize: "11px", color: "#555570", fontWeight: "400" }}>({m.relation})</span></div>
                  <div style={{ fontSize: "12px", color: "#8888a8", marginTop: "3px" }}>{m.mobile} · {m.email || "—"} · Aadhar: {maskAadhar(m.aadhar)}</div>
                </div>
              ))}
            </div>
          )}

          {/* Payment responsibility */}
          <div style={{
            background: "#0f0f13", border: "1px solid #2e2e40",
            borderRadius: "12px", padding: "14px 16px", marginBottom: "14px",
          }}>
            <div style={{ fontSize: "12px", color: "#8888a8", marginBottom: "10px", fontWeight: "600" }}>Payment Responsibility</div>
            <div style={{ display: "flex", gap: "8px" }}>
              {[
                { label: "🏠 Maintenance", pays: tenant.paysMaintenance, color: "#6c63ff" },
                { label: "💧 Water",       pays: tenant.paysWater,       color: "#06b6d4" },
              ].map(({ label, pays, color }) => (
                <div key={label} style={{
                  flex: 1, padding: "8px", borderRadius: "8px", textAlign: "center",
                  background: pays ? `${color}20` : "#1a1a24",
                  border: `1px solid ${pays ? `${color}60` : "#2e2e40"}`,
                }}>
                  <div style={{ fontSize: "11px", color: pays ? color : "#555570" }}>{label}</div>
                  <div style={{ fontSize: "11px", color: pays ? color : "#555570", marginTop: "3px" }}>
                    {pays ? "Tenant pays" : "Owner pays"}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {error   && <div style={{ background: "#ff525215", border: "1px solid #ff525240", borderRadius: "10px", padding: "10px 14px", fontSize: "13px", color: "#ff5252",  marginBottom: "12px" }}>⚠ {error}</div>}
          {success && <div style={{ background: "#4caf5015", border: "1px solid #4caf5040", borderRadius: "10px", padding: "10px 14px", fontSize: "13px", color: "#4caf50", marginBottom: "12px" }}>{success}</div>}

          {!showExitConfirm ? (
            <button onClick={() => { setShowExitConfirm(true); setExitDateInput(new Date().toISOString().split("T")[0]); setError(""); }} style={{
              width: "100%", background: "transparent", color: "#ff5252",
              border: "1px solid #ff525240", borderRadius: "10px",
              padding: "12px", fontSize: "14px", fontWeight: "600", cursor: "pointer",
            }}>
              🚪 Mark Tenant as Exited
            </button>
          ) : (
            <div style={{
              background: "#ff525210", border: "1px solid #ff525240",
              borderRadius: "12px", padding: "14px 16px",
            }}>
              <div style={{ fontSize: "13px", fontWeight: "600", color: "#ff5252", marginBottom: "4px" }}>
                Confirm Tenant Exit
              </div>
              <div style={{ fontSize: "11px", color: "#8888a8", marginBottom: "12px" }}>
                Enter the actual date the tenant vacated the flat. This will be used to calculate final dues.
              </div>
              <label style={{ display: "block", fontSize: "11px", color: "#8888a8", marginBottom: "5px" }}>
                Exit Date *
              </label>
              <input
                type="date"
                value={exitDateInput}
                max={new Date().toISOString().split("T")[0]}
                onChange={e => { setExitDateInput(e.target.value); setError(""); }}
                style={{
                  width: "100%", background: "#0f0f13", border: "1px solid #2e2e40",
                  borderRadius: "10px", padding: "10px 12px", color: "#e8e8f0",
                  fontSize: "14px", outline: "none", boxSizing: "border-box", marginBottom: "12px",
                }}
              />
              <div style={{ fontSize: "11px", color: "#ffa726", marginBottom: "12px" }}>
                ⚠ Tenant login will be deactivated immediately. Any pending dues become the flat owner's responsibility.
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button onClick={() => { setShowExitConfirm(false); setError(""); }} style={{
                  flex: 1, background: "transparent", color: "#8888a8",
                  border: "1px solid #2e2e40", borderRadius: "10px",
                  padding: "11px", fontSize: "13px", fontWeight: "600", cursor: "pointer",
                }}>
                  Cancel
                </button>
                <button onClick={handleExitTenant} disabled={exiting} style={{
                  flex: 1, background: "#ff525220", color: "#ff5252",
                  border: "1px solid #ff525240", borderRadius: "10px",
                  padding: "11px", fontSize: "13px", fontWeight: "600",
                  cursor: exiting ? "not-allowed" : "pointer", opacity: exiting ? 0.7 : 1,
                }}>
                  {exiting ? "Processing…" : "Confirm Exit"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── ADD TENANT FORM ── */}
      {view === "none" && (
        <div>
          <div style={{ fontSize: "12px", color: "#8888a8", marginBottom: "14px", lineHeight: "1.6" }}>
            Add minimum 2 persons. Select who is the <strong style={{ color: "#6c63ff" }}>Primary Tenant</strong> — the other(s) become family members automatically.
          </div>

          {/* Entry date */}
          <div style={fieldStyle}>
            <label style={labelStyle}>Entry Date *</label>
            <input style={inputStyle} type="date" value={entryDate} onChange={e => { setError(""); setEntryDate(e.target.value); }} />
          </div>

          {/* Persons */}
          {persons.map((p, idx) => (
            <div key={idx} style={{
              background: primaryIdx === idx ? "#6c63ff12" : "#0f0f13",
              border: `1px solid ${primaryIdx === idx ? "#6c63ff60" : "#2e2e40"}`,
              borderRadius: "12px", padding: "14px", marginBottom: "12px",
            }}>
              {/* Person header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <div style={{ fontSize: "13px", fontWeight: "700", color: primaryIdx === idx ? "#6c63ff" : "#8888a8" }}>
                  {primaryIdx === idx ? "👑 Primary Tenant" : `Person ${idx + 1}`}
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  {primaryIdx !== idx && (
                    <button onClick={() => setPrimaryIdx(idx)} style={{
                      background: "#6c63ff20", color: "#6c63ff", border: "1px solid #6c63ff40",
                      borderRadius: "6px", padding: "4px 10px", fontSize: "11px", cursor: "pointer",
                    }}>
                      Set as Primary
                    </button>
                  )}
                  {persons.length > 2 && (
                    <button onClick={() => removePerson(idx)} style={{
                      background: "#ff525215", color: "#ff5252", border: "1px solid #ff525240",
                      borderRadius: "6px", padding: "4px 10px", fontSize: "11px", cursor: "pointer",
                    }}>
                      Remove
                    </button>
                  )}
                </div>
              </div>

              <div style={fieldStyle}>
                <label style={labelStyle}>Full Name *</label>
                <input style={inputStyle} value={p.name} onChange={e => updatePerson(idx, "name", e.target.value)} placeholder="Full name" />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Mobile * (10 digits)</label>
                <input style={inputStyle} value={p.mobile} onChange={e => updatePerson(idx, "mobile", e.target.value.replace(/\D/g,"").slice(0,10))} placeholder="Mobile number" inputMode="numeric" />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Email *</label>
                <input style={inputStyle} type="email" value={p.email} onChange={e => updatePerson(idx, "email", e.target.value)} placeholder="Email address" />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Aadhar * (12 digits — stored securely, shown as XXXX XXXX 3456)</label>
                <input style={inputStyle} value={p.aadhar} onChange={e => updatePerson(idx, "aadhar", e.target.value.replace(/\D/g,"").slice(0,12))} placeholder="12-digit Aadhar number" inputMode="numeric" />
              </div>
              {idx !== primaryIdx && (
                <div style={fieldStyle}>
                  <label style={labelStyle}>Relation to Primary Tenant *</label>
                  <select style={{ ...inputStyle, cursor: "pointer" }} value={p.relation} onChange={e => updatePerson(idx, "relation", e.target.value)}>
                    <option value="">Select relation</option>
                    {RELATIONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              )}
            </div>
          ))}

          {/* Add person button */}
          <button onClick={addPerson} style={{
            width: "100%", background: "transparent", color: "#8888a8",
            border: "1px dashed #2e2e40", borderRadius: "10px",
            padding: "11px", fontSize: "13px", cursor: "pointer", marginBottom: "16px",
          }}>
            + Add Another Person
          </button>

          {/* Payment responsibility */}
          <div style={sectionHdr}>Payment Responsibility</div>
          <div style={{ fontSize: "11px", color: "#555570", marginBottom: "10px" }}>
            Select what the tenant pays. Owner remains liable to society for any unpaid dues.
          </div>
          <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
            {[
              { key: "maint", label: "🏠 Maintenance", val: paysMaint, set: setPaysMaint, color: "#6c63ff" },
              { key: "water", label: "💧 Water",       val: paysWater, set: setPaysWater, color: "#06b6d4" },
            ].map(({ key, label, val, set, color }) => (
              <button key={key} onClick={() => set(v => !v)} style={{
                flex: 1, padding: "12px 8px", borderRadius: "10px", fontSize: "12px",
                fontWeight: "600", cursor: "pointer", border: "1px solid",
                background: val ? `${color}20` : "transparent",
                borderColor: val ? `${color}60` : "#2e2e40",
                color: val ? color : "#555570",
              }}>
                {label}<br />
                <span style={{ fontSize: "10px", fontWeight: "400" }}>{val ? "Tenant pays" : "Owner pays"}</span>
              </button>
            ))}
          </div>

          {error   && <div style={{ background: "#ff525215", border: "1px solid #ff525240", borderRadius: "10px", padding: "10px 14px", fontSize: "13px", color: "#ff5252",  marginBottom: "12px" }}>⚠ {error}</div>}
          {success && <div style={{ background: "#4caf5015", border: "1px solid #4caf5040", borderRadius: "10px", padding: "10px 14px", fontSize: "13px", color: "#4caf50", marginBottom: "12px" }}>{success}</div>}

          <button onClick={handleAddTenant} disabled={saving} style={{
            width: "100%", background: "#6c63ff", color: "#fff", border: "none",
            borderRadius: "10px", padding: "13px", fontSize: "15px", fontWeight: "600",
            cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1,
          }}>
            {saving ? "Adding Tenant…" : "Add Tenant & Send Login Link"}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Owner Family Members Panel ────────────────────────────────
function OwnerFamily({ flat, canEdit, currentUser }) {
  const [ownerDoc, setOwnerDoc]     = useState(null);
  const [members, setMembers]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState("");
  const [success, setSuccess]       = useState("");
  const [showForm, setShowForm]     = useState(false);
  const [dobValue, setDobValue]     = useState("");
  const [dobSaving, setDobSaving]   = useState(false);
  const [dobSaved, setDobSaved]     = useState(false);
  const [dobError, setDobError]     = useState("");

  const isOwner = currentUser?.flatId === flat.flatNo && currentUser?.memberType === "owner";
  const canManage = (currentUser?.role === "President") || isOwner;

  useEffect(() => { loadOwner(); }, []);

  async function loadOwner() {
    try {
      const q = query(
        collection(db, "users"),
        where("flatId", "==", flat.flatNo),
        where("memberType", "==", "owner")
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        const data = { uid: snap.docs[0].id, ...snap.docs[0].data() };
        setOwnerDoc(data);
        setMembers(data.familyMembers || []);
        setDobValue(data.dob || "");
      }
    } catch (e) { console.error("Failed to load owner:", e); }
    finally { setLoading(false); }
  }

  async function saveMembers(updated) {
    if (!ownerDoc) return;
    setSaving(true);
    setError("");
    try {
      await updateDoc(doc(db, "users", ownerDoc.uid), { familyMembers: updated });
      setMembers(updated);
      setSuccess("✅ Family members saved.");
      setShowForm(false);
      setTimeout(() => setSuccess(""), 2500);
    } catch { setError("Save failed. Please try again."); }
    finally { setSaving(false); }
  }

  async function saveOwnDob() {
    if (!ownerDoc) return;
    setDobError("");
    if (dobValue && dobValue.trim() && !isValidDob(dobValue)) {
      setDobError("Please enter a valid date in dd-mm-yyyy format.");
      return;
    }
    setDobSaving(true);
    try {
      await updateDoc(doc(db, "users", ownerDoc.uid), { dob: dobValue.trim() });
      setOwnerDoc(prev => ({ ...prev, dob: dobValue.trim() }));
      setDobSaved(true);
      setTimeout(() => setDobSaved(false), 2500);
    } catch { setDobError("Save failed. Please try again."); }
    finally { setDobSaving(false); }
  }

  if (loading) return (
    <div style={{ textAlign: "center", padding: "30px", color: "#555570", fontSize: "13px" }}>Loading…</div>
  );

  if (!ownerDoc) return (
    <div style={{ textAlign: "center", padding: "30px", color: "#555570", fontSize: "13px" }}>
      No owner account found for this flat.
    </div>
  );

  return (
    <div>
      {/* Owner row — auto-populated, read-only */}
      <div style={{
        background: "#0f0f13", border: "1px solid #6c63ff40",
        borderRadius: "12px", padding: "14px 16px", marginBottom: "14px",
      }}>
        <div style={{ fontSize: "11px", fontWeight: "600", color: "#6c63ff", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
          👑 Owner
        </div>
        <div style={{ fontSize: "13px", fontWeight: "700", color: "#e8e8f0", marginBottom: "6px" }}>{ownerDoc.name}</div>
        {[
          ["Mobile",  ownerDoc.mobile  || "—"],
          ["Email",   ownerDoc.emailId || "—"],
          ["Aadhar",  maskAadhar(ownerDoc.aadhar)],
        ].map(([label, value]) => (
          <div key={label} style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
            <span style={{ fontSize: "12px", color: "#555570" }}>{label}</span>
            <span style={{ fontSize: "12px", color: "#8888a8" }}>{value}</span>
          </div>
        ))}

        {isOwner ? (
          <div style={{ marginTop: "10px", paddingTop: "10px", borderTop: "1px solid #2e2e4060" }}>
            <label style={{ display: "block", fontSize: "11px", color: "#8888a8", marginBottom: "5px" }}>
              My Birthday (optional, dd-mm-yyyy — add it to receive birthday wishes 🎂)
            </label>
            <div style={{ display: "flex", gap: "8px" }}>
              <input
                style={{
                  flex: 1, background: "#13131a", border: "1px solid #2e2e40",
                  borderRadius: "8px", padding: "9px 12px", color: "#e8e8f0",
                  fontSize: "12px", outline: "none", boxSizing: "border-box",
                }}
                value={dobValue}
                onChange={e => {
                  // Auto-inserts the dashes as they type, so they don't have to type "-" themselves.
                  let v = e.target.value.replace(/[^\d]/g, "").slice(0, 8);
                  if (v.length > 4) v = `${v.slice(0,2)}-${v.slice(2,4)}-${v.slice(4)}`;
                  else if (v.length > 2) v = `${v.slice(0,2)}-${v.slice(2)}`;
                  setDobValue(v);
                }}
                placeholder="dd-mm-yyyy" inputMode="numeric" maxLength={10}
              />
              <button onClick={saveOwnDob} disabled={dobSaving} style={{
                background: "#6c63ff20", color: "#6c63ff", border: "1px solid #6c63ff60",
                borderRadius: "8px", padding: "9px 14px", fontSize: "12px", fontWeight: "600",
                cursor: dobSaving ? "not-allowed" : "pointer", opacity: dobSaving ? 0.7 : 1,
              }}>{dobSaving ? "Saving…" : "Save"}</button>
            </div>
            {dobError && <div style={{ fontSize: "11px", color: "#ff5252", marginTop: "6px" }}>⚠ {dobError}</div>}
            {dobSaved && <div style={{ fontSize: "11px", color: "#4caf50", marginTop: "6px" }}>✅ Saved.</div>}
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
            <span style={{ fontSize: "12px", color: "#555570" }}>Birthday</span>
            <span style={{ fontSize: "12px", color: "#8888a8" }}>{ownerDoc.dob || "—"}</span>
          </div>
        )}
      </div>

      {/* Existing family members */}
      {members.length === 0 && !showForm && (
        <div style={{
          background: "#ffa72610", border: "1px solid #ffa72630",
          borderRadius: "10px", padding: "12px 14px", fontSize: "12px",
          color: "#ffa726", marginBottom: "14px", textAlign: "center",
        }}>
          ⚠ No family members added yet. Spouse details are mandatory.
        </div>
      )}

      {members.map((m, i) => (
        <div key={i} style={{
          background: "#0f0f13", border: "1px solid #2e2e40",
          borderRadius: "12px", padding: "14px 16px", marginBottom: "10px",
        }}>
          <div style={{ fontSize: "13px", fontWeight: "700", color: "#e8e8f0", marginBottom: "6px" }}>
            {m.name}
            <span style={{ fontSize: "11px", color: "#555570", fontWeight: "400", marginLeft: "6px" }}>({m.relation})</span>
          </div>
          {[
            ["Mobile",   m.mobile  || "—"],
            ["Email",    m.email   || "—"],
            ["Aadhar",   maskAadhar(m.aadhar)],
            ["Birthday", m.dob || "—"],
          ].map(([label, value]) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
              <span style={{ fontSize: "12px", color: "#555570" }}>{label}</span>
              <span style={{ fontSize: "12px", color: "#8888a8" }}>{value}</span>
            </div>
          ))}
        </div>
      ))}

      {success && <div style={{ background: "#4caf5015", border: "1px solid #4caf5040", borderRadius: "10px", padding: "10px 14px", fontSize: "13px", color: "#4caf50", marginBottom: "12px" }}>{success}</div>}
      {error   && <div style={{ background: "#ff525215", border: "1px solid #ff525240", borderRadius: "10px", padding: "10px 14px", fontSize: "13px", color: "#ff5252",  marginBottom: "12px" }}>⚠ {error}</div>}

      {/* Add/Edit button */}
      {canManage && !showForm && (
        <button onClick={() => setShowForm(true)} style={{
          width: "100%", background: "#6c63ff15", color: "#6c63ff",
          border: "1px solid #6c63ff40", borderRadius: "10px",
          padding: "12px", fontSize: "13px", fontWeight: "600", cursor: "pointer",
        }}>
          {members.length === 0 ? "➕ Add Family Members" : "✏️ Edit Family Members"}
        </button>
      )}

      {/* Inline edit form */}
      {canManage && showForm && (
        <FamilyMembersForm
          initialMembers={members}
          relationLabel="Relation to Owner"
          saving={saving}
          onSave={saveMembers}
          onCancel={() => { setShowForm(false); setError(""); }}
          requireSpouse={true}
        />
      )}
    </div>
  );
}

// ── Shared Family Members Form ────────────────────────────────
function FamilyMembersForm({ initialMembers, relationLabel, saving, onSave, onCancel, requireSpouse }) {
  const [persons, setPersons] = useState(
    initialMembers.length > 0 ? initialMembers.map(m => ({ ...m })) : [emptyPerson()]
  );
  const [error, setError] = useState("");

  const inputStyle = {
    width: "100%", background: "#0f0f13", border: "1px solid #2e2e40",
    borderRadius: "10px", padding: "11px 14px", color: "#e8e8f0",
    fontSize: "14px", outline: "none", boxSizing: "border-box",
  };
  const labelStyle = { display: "block", fontSize: "12px", color: "#8888a8", marginBottom: "5px" };
  const fieldStyle = { marginBottom: "12px" };

  function update(idx, key, value) {
    setError("");
    setPersons(prev => prev.map((p, i) => i === idx ? { ...p, [key]: value } : p));
  }

  function addPerson() { setPersons(prev => [...prev, emptyPerson()]); }

  function removePerson(idx) {
    if (persons.length <= 1) return;
    setPersons(prev => prev.filter((_, i) => i !== idx));
  }

  function validate() {
    for (let i = 0; i < persons.length; i++) {
      const p = persons[i];
      if (!p.name.trim())  { setError(`Person ${i+1}: Name is required.`); return false; }
      if (!/^\d{10}$/.test(p.mobile.trim())) { setError(`Person ${i+1}: Valid 10-digit mobile required.`); return false; }
      if (!p.email.trim()) { setError(`Person ${i+1}: Email is required.`); return false; }
      // Aadhar is optional here. If they typed something, it must still be a valid 12-digit number.
      if (p.aadhar && p.aadhar.replace(/\s/g,"").length !== 12) { setError(`Person ${i+1}: Aadhar, if entered, must be 12 digits.`); return false; }
      if (!p.relation)     { setError(`Person ${i+1}: Relation is required.`); return false; }
      // Birthday is optional. If they typed something, it must be a real dd-mm-yyyy date.
      if (p.dob && p.dob.trim() && !isValidDob(p.dob)) { setError(`Person ${i+1}: Birthday, if entered, must be a valid date in dd-mm-yyyy format.`); return false; }
    }
    if (requireSpouse) {
      const hasSpouse = persons.some(p => p.relation === "Spouse");
      if (!hasSpouse) { setError("Spouse details are mandatory. Please add at least one Spouse."); return false; }
    }
    return true;
  }

  function handleSave() {
    if (!validate()) return;
    onSave(persons.map(p => ({
      name:     p.name.trim(),
      mobile:   p.mobile.trim(),
      email:    p.email.trim(),
      aadhar:   p.aadhar.replace(/\s/g, ""),
      relation: p.relation,
      dob:      p.dob.trim(),
    })));
  }

  return (
    <div>
      {persons.map((p, idx) => (
        <div key={idx} style={{
          background: "#0f0f13", border: "1px solid #2e2e40",
          borderRadius: "12px", padding: "14px", marginBottom: "12px",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
            <div style={{ fontSize: "13px", fontWeight: "700", color: "#8888a8" }}>Person {idx + 1}</div>
            {persons.length > 1 && (
              <button onClick={() => removePerson(idx)} style={{
                background: "#ff525215", color: "#ff5252", border: "1px solid #ff525240",
                borderRadius: "6px", padding: "4px 10px", fontSize: "11px", cursor: "pointer",
              }}>Remove</button>
            )}
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Full Name *</label>
            <input style={inputStyle} value={p.name} onChange={e => update(idx, "name", e.target.value)} placeholder="Full name" />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Mobile * (10 digits)</label>
            <input style={inputStyle} value={p.mobile} onChange={e => update(idx, "mobile", e.target.value.replace(/\D/g,"").slice(0,10))} placeholder="Mobile number" inputMode="numeric" />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Email *</label>
            <input style={inputStyle} type="email" value={p.email} onChange={e => update(idx, "email", e.target.value)} placeholder="Email address" />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Aadhar (optional, 12 digits — shown as XXXX XXXX 3456)</label>
            <input style={inputStyle} value={p.aadhar} onChange={e => update(idx, "aadhar", e.target.value.replace(/\D/g,"").slice(0,12))} placeholder="12-digit Aadhar number (optional)" inputMode="numeric" />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>{relationLabel} *</label>
            <select style={{ ...inputStyle, cursor: "pointer" }} value={p.relation} onChange={e => update(idx, "relation", e.target.value)}>
              <option value="">Select relation</option>
              {RELATIONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Birthday (optional, dd-mm-yyyy — add it to receive birthday wishes 🎂)</label>
            <input style={inputStyle} value={p.dob}
              onChange={e => {
                // Auto-inserts the dashes as they type, so they don't have to type "-" themselves.
                let v = e.target.value.replace(/[^\d]/g, "").slice(0, 8);
                if (v.length > 4) v = `${v.slice(0,2)}-${v.slice(2,4)}-${v.slice(4)}`;
                else if (v.length > 2) v = `${v.slice(0,2)}-${v.slice(2)}`;
                update(idx, "dob", v);
              }}
              placeholder="dd-mm-yyyy" inputMode="numeric" maxLength={10} />
          </div>
        </div>
      ))}

      <button onClick={addPerson} style={{
        width: "100%", background: "transparent", color: "#8888a8",
        border: "1px dashed #2e2e40", borderRadius: "10px",
        padding: "11px", fontSize: "13px", cursor: "pointer", marginBottom: "16px",
      }}>
        + Add Another Person
      </button>

      {error && <div style={{ background: "#ff525215", border: "1px solid #ff525240", borderRadius: "10px", padding: "10px 14px", fontSize: "13px", color: "#ff5252", marginBottom: "12px" }}>⚠ {error}</div>}

      <div style={{ display: "flex", gap: "8px" }}>
        <button onClick={onCancel} style={{
          flex: 1, background: "transparent", color: "#8888a8",
          border: "1px solid #2e2e40", borderRadius: "10px",
          padding: "12px", fontSize: "13px", fontWeight: "600", cursor: "pointer",
        }}>Cancel</button>
        <button onClick={handleSave} disabled={saving} style={{
          flex: 2, background: "#6c63ff", color: "#fff", border: "none",
          borderRadius: "10px", padding: "12px", fontSize: "14px", fontWeight: "600",
          cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1,
        }}>
          {saving ? "Saving…" : "Save Family Members"}
        </button>
      </div>
    </div>
  );
}

// ── Flat Detail Modal ──────────────────────────────────────────
function FlatDetail({ flat, canEdit, currentUser, onClose, onSave, settings }) {
  const [form, setForm]         = useState({ ...flat });
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [error, setError]       = useState("");
  const [activeTab, setActiveTab] = useState("basic");
  const [arrearsUnlocked, setArrearsUnlocked] = useState(false);
  const [showArrearsWarning, setShowArrearsWarning] = useState(false);

  const isThisFlatOwner = currentUser?.flatId === flat.flatNo;
  const arrearsAlreadySaved = flat.openingArrearsMonths !== undefined && flat.openingArrearsMonths !== null;
  const arrearsIsLocked = arrearsAlreadySaved && !arrearsUnlocked;

  function change(key, value) { setSaved(false); setError(""); setForm(prev => ({ ...prev, [key]: value })); }

  async function handleSave() {
    if (!form.flatNo.trim()) { setError("Flat number is required."); return; }
    setSaving(true);
    setError("");
    try {
      const toSave = { ...form };
      delete toSave.maintenanceRate;
      await setDoc(doc(db, "flats", flat.flatNo), toSave, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onSave(toSave);
    } catch { setError("Save failed. Check your connection."); }
    finally { setSaving(false); }
  }

  const Field = ({ label, fieldKey, type = "text" }) => (
    <div style={{ marginBottom: "12px" }}>
      <label style={{ display: "block", fontSize: "12px", color: "#8888a8", marginBottom: "5px" }}>{label}</label>
      <input
        type={type} value={form[fieldKey] ?? ""}
        onChange={e => change(fieldKey, e.target.value)}
        readOnly={!canEdit}
        style={{
          width: "100%", background: !canEdit ? "#13131a" : "#0f0f13",
          border: "1px solid #2e2e40", borderRadius: "10px", padding: "11px 14px",
          color: !canEdit ? "#666680" : "#e8e8f0",
          fontSize: "14px", outline: "none", boxSizing: "border-box",
          cursor: !canEdit ? "default" : "text",
        }}
      />
    </div>
  );

  const canSeeTransfer  = ["President", "Secretary", "Adyantam"].includes(currentUser?.role);
  const canSeeException = currentUser?.role === "President";

  const TABS = [
    { key: "basic",      label: "🏠 Basic"    },
    { key: "legal",      label: "📋 Legal"    },
    { key: "family",     label: "👨‍👩‍👧 Family"  },
    ...(canSeeTransfer  ? [{ key: "transfer",  label: "🔄 Transfer"  }] : []),
    ...(canSeeException ? [{ key: "exception", label: "⚡ Exception" }] : []),
  ];

  return (
    <div style={{
      position: "fixed", inset: 0, background: "#000000cc", zIndex: 200,
      display: "flex", alignItems: "flex-end", justifyContent: "center",
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: "#1a1a24", borderRadius: "20px 20px 0 0", border: "1px solid #2e2e40",
        width: "100%", maxWidth: "480px", padding: "20px", maxHeight: "90vh", overflowY: "auto",
      }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <div>
            <div style={{ fontSize: "16px", fontWeight: "700", color: "#e8e8f0" }}>Flat {flat.flatNo}</div>
            <div style={{ fontSize: "11px", color: "#555570", marginTop: "2px" }}>
              {canEdit ? "Tap fields to edit" : "Your flat details"}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "#2e2e40", border: "none", borderRadius: "8px",
            color: "#8888a8", fontSize: "13px", padding: "6px 12px", cursor: "pointer",
          }}>Close</button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: "6px", marginBottom: "18px" }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
              flex: 1, padding: "9px 4px", borderRadius: "10px", fontSize: "12px",
              fontWeight: "600", cursor: "pointer", border: "1px solid",
              background: activeTab === t.key ? "#6c63ff20" : "transparent",
              borderColor: activeTab === t.key ? "#6c63ff60" : "#2e2e40",
              color: activeTab === t.key ? "#6c63ff" : "#555570",
            }}>{t.label}</button>
          ))}
        </div>

        {/* ── BASIC TAB ── */}
        {activeTab === "basic" && (
          <div>
            {canEdit && (
              <div style={{ marginBottom: "12px" }}>
                <label style={{ display: "block", fontSize: "12px", color: "#8888a8", marginBottom: "5px" }}>Status</label>
                <div style={{ display: "flex", gap: "8px" }}>
                  {["Occupied","Vacant"].map(s => (
                    <button key={s} onClick={() => change("status", s)} style={{
                      flex: 1, padding: "10px", borderRadius: "10px", fontSize: "13px", fontWeight: "600",
                      cursor: "pointer", border: "1px solid",
                      background: form.status === s ? (s === "Occupied" ? "#4caf5020" : "#ffa72620") : "transparent",
                      borderColor: form.status === s ? (s === "Occupied" ? "#4caf5060" : "#ffa72660") : "#2e2e40",
                      color: form.status === s ? (s === "Occupied" ? "#4caf50" : "#ffa726") : "#555570",
                    }}>{s}</button>
                  ))}
                </div>
              </div>
            )}
            <Field label="Owner Name"           fieldKey="ownerName" />
            <Field label="Representative"       fieldKey="representative" />
            <Field label="Floor"                fieldKey="floor" />
            <Field label="Block"                fieldKey="block" />
            <Field label="Door Number"          fieldKey="doorNo" />
            <div style={{ marginBottom: "12px" }}>
              <label style={{ display: "block", fontSize: "12px", color: "#8888a8", marginBottom: "5px" }}>
                Maintenance Rate (₹) — calculated from Settings
              </label>
              <div style={{
                width: "100%", background: "#13131a", border: "1px solid #2e2e40",
                borderRadius: "10px", padding: "11px 14px", color: "#666680",
                fontSize: "14px", boxSizing: "border-box",
              }}>
                ₹{computeMonthlyRate(form, settings).toLocaleString("en-IN")} / month
                <span style={{ fontSize: "11px", marginLeft: "6px" }}>
                  ({form.status === "Vacant" ? "Vacant rate" : "Occupied rate"})
                </span>
              </div>
            </div>

            <div style={{ marginBottom: "12px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "5px" }}>
                <label style={{ fontSize: "12px", color: "#8888a8" }}>
                  Opening Arrears (Months)
                </label>
                {arrearsIsLocked && currentUser?.role === "President" && (
                  <button
                    onClick={() => setShowArrearsWarning(true)}
                    style={{
                      background: "none", border: "none", color: "#ffa726",
                      fontSize: "11px", cursor: "pointer", padding: 0,
                    }}
                  >🔒 Locked — Unlock to correct</button>
                )}
              </div>
              <input
                type="number" min="0" step="1"
                value={form.openingArrearsMonths ?? 0}
                onChange={e => change("openingArrearsMonths", e.target.value)}
                readOnly={!canEdit || arrearsIsLocked}
                style={{
                  width: "100%", background: (!canEdit || arrearsIsLocked) ? "#13131a" : "#0f0f13",
                  border: "1px solid #2e2e40", borderRadius: "10px", padding: "11px 14px",
                  color: (!canEdit || arrearsIsLocked) ? "#666680" : "#e8e8f0",
                  fontSize: "14px", outline: "none", boxSizing: "border-box",
                  cursor: (!canEdit || arrearsIsLocked) ? "default" : "text",
                }}
              />
              <div style={{ fontSize: "11px", color: "#555570", marginTop: "4px" }}>
                Backlog before April 2026, in whole months. Leave 0 if none.
              </div>

              {showArrearsWarning && (
                <div style={{
                  marginTop: "10px", background: "#ffa72615", border: "1px solid #ffa72640",
                  borderRadius: "10px", padding: "12px 14px",
                }}>
                  <div style={{ fontSize: "12px", color: "#ffa726", marginBottom: "10px" }}>
                    ⚠ This will unlock Opening Arrears for editing. Only do this to correct a
                    genuine mistake — changing it after dues have been calculated will shift all
                    outstanding amounts for this flat.
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      onClick={() => setShowArrearsWarning(false)}
                      style={{
                        flex: 1, background: "transparent", border: "1px solid #2e2e40",
                        borderRadius: "8px", padding: "8px", fontSize: "12px",
                        color: "#8888a8", cursor: "pointer",
                      }}
                    >Cancel</button>
                    <button
                      onClick={() => { setArrearsUnlocked(true); setShowArrearsWarning(false); }}
                      style={{
                        flex: 1, background: "#ffa72620", border: "1px solid #ffa72660",
                        borderRadius: "8px", padding: "8px", fontSize: "12px",
                        color: "#ffa726", cursor: "pointer", fontWeight: "600",
                      }}
                    >Yes, Unlock</button>
                  </div>
                </div>
              )}
            </div>

            {error && <div style={{ background: "#ff525215", border: "1px solid #ff525240", borderRadius: "10px", padding: "10px 14px", fontSize: "13px", color: "#ff5252", marginBottom: "12px" }}>⚠ {error}</div>}
            {saved && <div style={{ background: "#4caf5015", border: "1px solid #4caf5040", borderRadius: "10px", padding: "10px 14px", fontSize: "13px", color: "#4caf50", marginBottom: "12px" }}>✅ Saved successfully.</div>}
            {canEdit && (
              <button onClick={handleSave} disabled={saving} style={{
                width: "100%", background: "#6c63ff", color: "#fff", border: "none",
                borderRadius: "10px", padding: "13px", fontSize: "15px", fontWeight: "600",
                cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1,
              }}>{saving ? "Saving…" : "Save Changes"}</button>
            )}
          </div>
        )}

        {/* ── LEGAL TAB ── */}
        {activeTab === "legal" && (
          <div>
            <Field label="Property Tax No"     fieldKey="ptinNo"    />
            <Field label="Electricity Meter No" fieldKey="serviceNo" />
            <Field label="Electricity USC No"  fieldKey="uscNo"     />

            {error && <div style={{ background: "#ff525215", border: "1px solid #ff525240", borderRadius: "10px", padding: "10px 14px", fontSize: "13px", color: "#ff5252", marginBottom: "12px" }}>⚠ {error}</div>}
            {saved && <div style={{ background: "#4caf5015", border: "1px solid #4caf5040", borderRadius: "10px", padding: "10px 14px", fontSize: "13px", color: "#4caf50", marginBottom: "12px" }}>✅ Saved successfully.</div>}
            {canEdit && (
              <button onClick={handleSave} disabled={saving} style={{
                width: "100%", background: "#6c63ff", color: "#fff", border: "none",
                borderRadius: "10px", padding: "13px", fontSize: "15px", fontWeight: "600",
                cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1,
              }}>{saving ? "Saving…" : "Save Changes"}</button>
            )}
          </div>
        )}

        {/* ── FAMILY TAB ── */}
        {activeTab === "family" && (
          <div>
            <OwnerFamily flat={flat} canEdit={canEdit} currentUser={currentUser} />

            {/* Manage Tenant — flat owner of THIS flat only */}
            {isThisFlatOwner && (
              <div style={{ marginTop: "20px", borderTop: "1px solid #2e2e40", paddingTop: "16px" }}>
                <div style={{ fontSize: "12px", fontWeight: "600", color: "#8888a8", marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Tenant
                </div>
                <ManageTenant flat={flat} currentUser={currentUser} onClose={onClose} />
              </div>
            )}
          </div>
        )}

        {/* ── TRANSFER TAB ── */}
        {activeTab === "transfer" && canSeeTransfer && (
          <OwnershipTransfer flat={flat} currentUser={currentUser} />
        )}

        {/* ── EXCEPTION TAB ── */}
        {activeTab === "exception" && canSeeException && (
          <MaintenanceException flat={flat} currentUser={currentUser} settings={settings} />
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// OWNERSHIP TRANSFER PANEL
// ══════════════════════════════════════════════════════════════
function OwnershipTransfer({ flat, currentUser }) {
  const isPresident = currentUser?.role === "President";
  const isSecretary = currentUser?.role === "Secretary";

  const [pending,   setPending]   = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState("");
  const [success,   setSuccess]   = useState("");

  const [form, setForm] = useState({
    newOwnerName: "", newOwnerMobile: "", newOwnerEmail: "",
    newOwnerPassword: "", saleDate: "",
  });

  useEffect(() => { loadPending(); }, []);

  async function loadPending() {
    try {
      const { doc: fdoc, getDoc } = await import("firebase/firestore");
      const flatSnap = await getDoc(fdoc(db, "flats", flat.flatNo));
      if (flatSnap.exists()) setPending(flatSnap.data().pendingTransfer || null);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  function fc(key, value) { setError(""); setForm(prev => ({ ...prev, [key]: value })); }

  async function handleInitiate() {
    if (!form.newOwnerName.trim())                          { setError("New owner name is required.");      return; }
    if (!/^\d{10}$/.test(form.newOwnerMobile.trim()))      { setError("Valid 10-digit mobile required."); return; }
    if (!form.newOwnerEmail.trim())                         { setError("New owner email is required.");     return; }
    if (form.newOwnerPassword.trim().length < 6)            { setError("Password must be at least 6 characters."); return; }
    if (!form.saleDate)                                     { setError("Sale date is required.");           return; }

    setSaving(true); setError("");
    try {
      const transfer = {
        newOwnerName:     form.newOwnerName.trim(),
        newOwnerMobile:   form.newOwnerMobile.trim(),
        newOwnerEmail:    form.newOwnerEmail.trim(),
        newOwnerPassword: form.newOwnerPassword.trim(),
        saleDate:         form.saleDate,
        initiatedBy:      currentUser.name,
        initiatedByUid:   currentUser.uid,
        initiatedAt:      new Date().toISOString(),
        status:           "pending",
      };

      await updateDoc(doc(db, "flats", flat.flatNo), { pendingTransfer: transfer });

      // Bell notification → President
      const presSnap = await getDocs(query(collection(db, "users"), where("role", "==", "President")));
      await Promise.all(presSnap.docs.map(d =>
        addDoc(collection(db, "notifications"), {
          toUid:     d.id,
          type:      "ownership_transfer_pending",
          message:   `Flat ${flat.flatNo} — Ownership transfer to ${form.newOwnerName.trim()} is pending your approval.`,
          flatId:    flat.flatNo,
          isRead:    false,
          createdAt: serverTimestamp(),
        })
      ));

      setPending(transfer);
      setShowForm(false);
      setSuccess("✅ Transfer initiated. President has been notified.");
      setTimeout(() => setSuccess(""), 3500);
    } catch (e) {
      console.error(e);
      setError("Failed to initiate transfer. Please try again.");
    } finally { setSaving(false); }
  }

  async function handleWithdraw() {
    if (!window.confirm("Withdraw this transfer request? The current owner remains active.")) return;
    setSaving(true); setError("");
    try {
      const { deleteField } = await import("firebase/firestore");
      await updateDoc(doc(db, "flats", flat.flatNo), { pendingTransfer: deleteField() });
      setPending(null);
      setSuccess("✅ Transfer withdrawn.");
      setTimeout(() => setSuccess(""), 2500);
    } catch { setError("Failed to withdraw. Please try again."); }
    finally { setSaving(false); }
  }

  async function handleApprove() {
    if (!window.confirm(
      `Approve ownership transfer of Flat ${flat.flatNo} to ${pending.newOwnerName}?\n\nThis will:\n• Create a new owner account\n• Send login link to their email\n• Archive old owner's family members\n• Auto-lock old owner in 30 days`
    )) return;

    setSaving(true); setError("");
    try {
      const bcrypt = (await import("bcryptjs")).default;
      const { deleteField } = await import("firebase/firestore");

      // 1. Hash new owner's temporary password
      const hashed = await bcrypt.hash(pending.newOwnerPassword, 10);

      // 2. Find old active owner → archive family members + set 30-day auto-lock
      const oldOwnerSnap = await getDocs(query(
        collection(db, "users"),
        where("flatId",     "==", flat.flatNo),
        where("memberType", "==", "owner"),
        where("active",     "==", true)
      ));
      if (!oldOwnerSnap.empty) {
        const oldDoc  = oldOwnerSnap.docs[0];
        const oldData = oldDoc.data();
        const autoLockDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
        await updateDoc(doc(db, "users", oldDoc.id), {
          archivedFamilyMembers: oldData.familyMembers || [],
          familyMembers:         [],
          autoLockAt:            Timestamp.fromDate(autoLockDate),
          transferredFlatId:     flat.flatNo,
          transferredAt:         new Date().toISOString(),
        });
      }

      // 3. Generate 72-hour reset token for new owner
      const tokenArray = new Uint8Array(16);
      window.crypto.getRandomValues(tokenArray);
      const token  = Array.from(tokenArray).map(b => b.toString(16).padStart(2, "0")).join("");
      const expiry = new Date(Date.now() + 72 * 60 * 60 * 1000);

      // 4. Create new owner account in users collection
      await addDoc(collection(db, "users"), {
        name:             pending.newOwnerName,
        mobile:           pending.newOwnerMobile,
        emailId:          pending.newOwnerEmail,
        password:         hashed,
        role:             "FlatOwner",
        memberType:       "owner",
        flatId:           flat.flatNo,
        active:           true,
        paysMaintenance:  false,
        paysWater:        false,
        upiIds:           [],
        familyMembers:    [],
        failedAttempts:   0,
        lockedUntil:      null,
        resetToken:       token,
        resetTokenExpiry: Timestamp.fromDate(expiry),
        createdAt:        serverTimestamp(),
        createdBy:        currentUser.name,
      });

      // 5. Update flat document — new owner name + remove pendingTransfer
      await updateDoc(doc(db, "flats", flat.flatNo), {
        ownerName:       pending.newOwnerName,
        legalOwnerName:  pending.newOwnerName,
        representative:  pending.newOwnerName,
        pendingTransfer: deleteField(),
      });

      // 6. Send welcome / password-setup email to new owner
      try {
        const resetLink = `${window.location.origin}/reset-password?token=${token}`;
        await emailjs.send(
          EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID,
          { to_email: pending.newOwnerEmail, to_name: pending.newOwnerName, link: resetLink },
          { publicKey: EMAILJS_PUBLIC_KEY }
        );
      } catch (emailErr) { console.warn("Welcome email failed:", emailErr); }

      // 7. Bell notification → Secretary
      const secSnap = await getDocs(query(collection(db, "users"), where("role", "==", "Secretary")));
      await Promise.all(secSnap.docs.map(d =>
        addDoc(collection(db, "notifications"), {
          toUid:     d.id,
          type:      "ownership_transfer_approved",
          message:   `Flat ${flat.flatNo} transfer to ${pending.newOwnerName} approved. Login link sent to their email.`,
          flatId:    flat.flatNo,
          isRead:    false,
          createdAt: serverTimestamp(),
        })
      ));

      const approvedName = pending.newOwnerName;
      const approvedEmail = pending.newOwnerEmail;
      setPending(null);
      setSuccess(`✅ Approved! Login link sent to ${approvedEmail}. Old owner auto-locks in 30 days.`);
      setTimeout(() => setSuccess(""), 6000);
    } catch (e) {
      console.error(e);
      setError("Approval failed. Please try again.");
    } finally { setSaving(false); }
  }

  const inp = {
    width: "100%", background: "#0f0f13", border: "1px solid #2e2e40",
    borderRadius: "10px", padding: "11px 14px", color: "#e8e8f0",
    fontSize: "14px", outline: "none", boxSizing: "border-box",
  };
  const lbl = { display: "block", fontSize: "12px", color: "#8888a8", marginBottom: "5px" };
  const fld = { marginBottom: "12px" };

  if (loading) return (
    <div style={{ textAlign: "center", padding: "30px", color: "#555570", fontSize: "13px" }}>Loading…</div>
  );

  return (
    <div>
      <div style={{ fontSize: "12px", color: "#555570", marginBottom: "16px", lineHeight: "1.6" }}>
        Use this tab when a flat is sold. Secretary initiates the transfer, President approves.
      </div>

      {/* Current owner card — always shown */}
      <div style={{
        background: "#0f0f13", border: "1px solid #2e2e40",
        borderRadius: "12px", padding: "14px 16px", marginBottom: "14px",
      }}>
        <div style={{ fontSize: "11px", fontWeight: "600", color: "#8888a8", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Current Owner</div>
        <div style={{ fontSize: "14px", fontWeight: "700", color: "#e8e8f0" }}>{flat.ownerName || "—"}</div>
        <div style={{ fontSize: "12px", color: "#555570", marginTop: "3px" }}>Flat {flat.flatNo}</div>
      </div>

      {success && <div style={{ background: "#4caf5015", border: "1px solid #4caf5040", borderRadius: "10px", padding: "10px 14px", fontSize: "13px", color: "#4caf50", marginBottom: "12px" }}>{success}</div>}
      {error   && <div style={{ background: "#ff525215", border: "1px solid #ff525240", borderRadius: "10px", padding: "10px 14px", fontSize: "13px", color: "#ff5252",  marginBottom: "12px" }}>⚠ {error}</div>}

      {/* ── NO PENDING TRANSFER ── */}
      {!pending && (
        <div>
          {isSecretary && !showForm && (
            <button onClick={() => setShowForm(true)} style={{
              width: "100%", background: "#6c63ff15", color: "#6c63ff",
              border: "1px solid #6c63ff40", borderRadius: "10px",
              padding: "12px", fontSize: "13px", fontWeight: "600", cursor: "pointer",
            }}>
              🔄 Initiate Ownership Transfer
            </button>
          )}

          {isPresident && (
            <div style={{
              background: "#4caf5010", border: "1px solid #4caf5030",
              borderRadius: "10px", padding: "12px 14px", fontSize: "12px",
              color: "#4caf50", textAlign: "center",
            }}>
              ✅ No pending transfer for this flat.
            </div>
          )}

          {/* Secretary initiation form */}
          {isSecretary && showForm && (
            <div style={{ marginTop: "4px" }}>
              <div style={{ fontSize: "13px", fontWeight: "700", color: "#e8e8f0", marginBottom: "14px" }}>New Owner Details</div>

              <div style={fld}>
                <label style={lbl}>Full Name *</label>
                <input style={inp} value={form.newOwnerName}
                  onChange={e => fc("newOwnerName", e.target.value)}
                  placeholder="New owner's full name" />
              </div>
              <div style={fld}>
                <label style={lbl}>Mobile * (10 digits)</label>
                <input style={inp} value={form.newOwnerMobile} inputMode="numeric"
                  onChange={e => fc("newOwnerMobile", e.target.value.replace(/\D/g,"").slice(0,10))}
                  placeholder="Mobile number" />
              </div>
              <div style={fld}>
                <label style={lbl}>Email * (login link will be sent here)</label>
                <input style={inp} type="email" value={form.newOwnerEmail}
                  onChange={e => fc("newOwnerEmail", e.target.value)}
                  placeholder="Email address" />
              </div>
              <div style={fld}>
                <label style={lbl}>Temporary Password * (min 6 characters)</label>
                <input style={inp} type="password" value={form.newOwnerPassword}
                  onChange={e => fc("newOwnerPassword", e.target.value)}
                  placeholder="Temporary password" />
              </div>
              <div style={fld}>
                <label style={lbl}>Sale Date *</label>
                <input style={inp} type="date" value={form.saleDate}
                  onChange={e => fc("saleDate", e.target.value)} />
              </div>

              <div style={{ display: "flex", gap: "8px", marginTop: "4px" }}>
                <button onClick={() => { setShowForm(false); setError(""); }} style={{
                  flex: 1, background: "transparent", color: "#8888a8",
                  border: "1px solid #2e2e40", borderRadius: "10px",
                  padding: "12px", fontSize: "13px", fontWeight: "600", cursor: "pointer",
                }}>Cancel</button>
                <button onClick={handleInitiate} disabled={saving} style={{
                  flex: 2, background: "#6c63ff", color: "#fff", border: "none",
                  borderRadius: "10px", padding: "12px", fontSize: "13px", fontWeight: "600",
                  cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1,
                }}>{saving ? "Submitting…" : "Submit for Approval"}</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── PENDING TRANSFER EXISTS ── */}
      {pending && (
        <div>
          <div style={{
            background: "#ffa72610", border: "1px solid #ffa72640",
            borderRadius: "10px", padding: "10px 14px", fontSize: "12px",
            color: "#ffa726", marginBottom: "14px", fontWeight: "600", textAlign: "center",
          }}>
            ⏳ Pending President's Approval
          </div>

          <div style={{
            background: "#0f0f13", border: "1px solid #2e2e40",
            borderRadius: "12px", padding: "14px 16px", marginBottom: "14px",
          }}>
            <div style={{ fontSize: "12px", fontWeight: "600", color: "#8888a8", marginBottom: "10px" }}>Transfer Details</div>
            {[
              ["New Owner",    pending.newOwnerName],
              ["Mobile",       pending.newOwnerMobile],
              ["Email",        pending.newOwnerEmail],
              ["Sale Date",    pending.saleDate],
              ["Initiated By", pending.initiatedBy],
              ["Initiated On", pending.initiatedAt ? new Date(pending.initiatedAt).toLocaleDateString("en-IN") : "—"],
            ].map(([label, value]) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                <span style={{ fontSize: "12px", color: "#555570" }}>{label}</span>
                <span style={{ fontSize: "12px", color: "#8888a8", textAlign: "right", maxWidth: "60%" }}>{value || "—"}</span>
              </div>
            ))}
          </div>

          {/* Secretary — can only withdraw */}
          {isSecretary && (
            <button onClick={handleWithdraw} disabled={saving} style={{
              width: "100%", background: "transparent", color: "#ff5252",
              border: "1px solid #ff525240", borderRadius: "10px",
              padding: "12px", fontSize: "13px", fontWeight: "600",
              cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1,
            }}>
              {saving ? "Processing…" : "↩ Withdraw Transfer Request"}
            </button>
          )}

          {/* President — approve */}
          {isPresident && (
            <div>
              <div style={{
                fontSize: "11px", color: "#555570", marginBottom: "12px",
                lineHeight: "1.7", background: "#0f0f13", border: "1px solid #2e2e40",
                borderRadius: "10px", padding: "10px 14px",
              }}>
                On approval:<br />
                • New owner account will be created<br />
                • Login setup link sent to their email<br />
                • Old owner's family members archived<br />
                • Old owner auto-locked after 30 days
              </div>
              <button onClick={handleApprove} disabled={saving} style={{
                width: "100%", background: "#4caf50", color: "#fff", border: "none",
                borderRadius: "10px", padding: "13px", fontSize: "14px", fontWeight: "600",
                cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1,
              }}>
                {saving ? "Processing…" : "✅ Approve Ownership Transfer"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAINTENANCE EXCEPTION PANEL
// ══════════════════════════════════════════════════════════════
function MaintenanceException({ flat, currentUser, settings }) {
  // FIX: The Apply Exception form saves fields directly onto the flat
  // document (exceptionActive, exceptionAmount, etc.) — not inside a
  // wrapped-up "maintenanceException" object. This now reads the real
  // saved fields, so an existing exception shows correctly after a
  // page refresh instead of always appearing as "No exception applied".
  const [exception, setException] = useState(
    flat.exceptionActive
      ? {
          exceptionActive:     flat.exceptionActive,
          exceptionAmount:     flat.exceptionAmount,
          exceptionApprovedBy: flat.exceptionApprovedBy,
          exceptionApprovedOn: flat.exceptionApprovedOn,
          exceptionNote:       flat.exceptionNote,
          exceptionPhoto:      flat.exceptionPhoto,
          exceptionRecalc:     flat.exceptionRecalc,
        }
      : null
  );
  const [showForm,    setShowForm]    = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState("");
  const [success,     setSuccess]     = useState("");

  // Form state
  const [amount,      setAmount]      = useState("");
  const [note,        setNote]        = useState("");
  const [photo,       setPhoto]       = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const inputStyle = {
    width: "100%", background: "#0f0f13", border: "1px solid #2e2e40",
    borderRadius: "10px", padding: "11px 14px", color: "#e8e8f0",
    fontSize: "14px", outline: "none", boxSizing: "border-box",
  };
  const labelStyle = { display: "block", fontSize: "12px", color: "#8888a8", marginBottom: "5px" };

  // Compress photo to base64
  function compressPhoto(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const maxPx  = 800;
          const scale  = Math.min(maxPx / img.width, maxPx / img.height, 1);
          canvas.width  = Math.round(img.width  * scale);
          canvas.height = Math.round(img.height * scale);
          canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL("image/jpeg", 0.7));
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async function handlePhotoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const b64 = await compressPhoto(file);
      setPhoto(b64);
    } catch { setError("Photo upload failed. Please try again."); }
    e.target.value = "";
  }

  function validate() {
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      setError("Please enter a valid amount greater than 0."); return false;
    }
    if (Number(amount) >= Number(computeMonthlyRate(flat, settings))) {
      setError("Exception amount must be less than the current maintenance rate."); return false;
    }
    if (!note.trim()) { setError("Please enter a short reason/note."); return false; }
    if (!photo)       { setError("Please attach the signed document photo."); return false; }
    return true;
  }

  async function handleApply() {
    if (!validate()) return;
    setSaving(true); setError("");
    try {
      const today        = new Date().toISOString().split("T")[0];
      const newAmount    = Number(amount);
      const oldAmount    = Number(computeMonthlyRate(flat, settings));
      const approvedOn   = today;
      const approvedBy   = currentUser.name;

      // 1. Fetch all unpaid maintenance payments for this flat
      const pSnap = await getDocs(query(
        collection(db, "payments"),
        where("flatId",  "==", flat.flatNo),
        where("type",    "==", "maintenance"),
        where("status",  "==", "pending")
      ));

      // 2. Build recalculation log
      const recalcMonths = pSnap.docs.map(d => ({
        paymentId: d.id,
        month:     d.data().month || d.data().label || d.id,
        oldAmt:    oldAmount,
        newAmt:    newAmount,
      }));

      const totalReduction = recalcMonths.length * (oldAmount - newAmount);

      // 3. Build exception object to store in flat doc
      const exceptionData = {
        exceptionActive:    true,
        exceptionAmount:    newAmount,
        exceptionApprovedBy: approvedBy,
        exceptionApprovedOn: approvedOn,
        exceptionNote:      note.trim(),
        exceptionPhoto:     photo,
        exceptionRecalc: {
          months:         recalcMonths,
          totalReduction,
          recalcDoneBy:   approvedBy,
          recalcDoneOn:   approvedOn,
        },
      };

      // 4. Update all pending payments to new amount in a batch
      const batch = writeBatch(db);
      pSnap.docs.forEach(d => {
        batch.update(doc(db, "payments", d.id), { amount: newAmount, exceptionApplied: true });
      });

      // 5. Save exception to flat document
      batch.update(doc(db, "flats", flat.flatNo), exceptionData);

      await batch.commit();

      setException(exceptionData);
      setShowForm(false);
      setShowConfirm(false);
      setSuccess(`✅ Exception applied. ${recalcMonths.length} pending dues recalculated. Total reduction: ₹${totalReduction.toLocaleString("en-IN")}.`);
    } catch (e) {
      console.error(e);
      setError("Failed to apply exception. Please try again.");
    } finally { setSaving(false); }
  }

  // Download recalculation report as plain text
  function downloadReport() {
    if (!exception?.exceptionRecalc) return;
    const r    = exception.exceptionRecalc;
    const lines = [
      "MAINTENANCE EXCEPTION REPORT",
      "==============================",
      `Flat No       : ${flat.flatNo}`,
      `Owner         : ${flat.ownerName || "—"}`,
      `Approved By   : ${exception.exceptionApprovedBy}`,
      `Approved On   : ${exception.exceptionApprovedOn}`,
      `Note          : ${exception.exceptionNote}`,
      "",
      `Old Rate      : ₹${(r.months[0]?.oldAmt || 0).toLocaleString("en-IN")} / month`,
      `New Rate      : ₹${exception.exceptionAmount.toLocaleString("en-IN")} / month`,
      "",
      "MONTHS RECALCULATED",
      "-------------------",
      ...r.months.map(m => `  ${m.month}  :  ₹${m.oldAmt} → ₹${m.newAmt}  (saved ₹${m.oldAmt - m.newAmt})`),
      "",
      `TOTAL REDUCTION : ₹${r.totalReduction.toLocaleString("en-IN")}`,
      "",
      `Report generated on : ${new Date().toLocaleDateString("en-IN")}`,
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `Exception_Flat${flat.flatNo}_${exception.exceptionApprovedOn}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── No exception yet ──
  if (!exception) {
    return (
      <div>
        {!showForm ? (
          <div>
            <div style={{
              background: "#ffa72610", border: "1px solid #ffa72630",
              borderRadius: "12px", padding: "16px", textAlign: "center", marginBottom: "16px",
            }}>
              <div style={{ fontSize: "13px", color: "#ffa726", fontWeight: "600", marginBottom: "6px" }}>
                No maintenance exception applied
              </div>
              <div style={{ fontSize: "11px", color: "#555570" }}>
                Use this only when all flat owners have signed approval for a reduced rate.
              </div>
            </div>
            <button onClick={() => { setShowForm(true); setError(""); }} style={{
              width: "100%", background: "#6c63ff", color: "#fff", border: "none",
              borderRadius: "10px", padding: "13px", fontSize: "14px", fontWeight: "600", cursor: "pointer",
            }}>
              ⚡ Apply Exception
            </button>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: "13px", fontWeight: "700", color: "#e8e8f0", marginBottom: "4px" }}>
              Apply Maintenance Exception — Flat {flat.flatNo}
            </div>
            <div style={{ fontSize: "11px", color: "#ffa726", marginBottom: "16px" }}>
              ⚠ This cannot be reversed. Current rate: ₹{Number(computeMonthlyRate(flat, settings)).toLocaleString("en-IN")}/month
            </div>

            <div style={{ marginBottom: "12px" }}>
              <label style={labelStyle}>Approved Amount ₹ (new rate per month) *</label>
              <input
                type="number" min="1"
                value={amount}
                onChange={e => { setError(""); setAmount(e.target.value); }}
                placeholder="e.g. 800"
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: "12px" }}>
              <label style={labelStyle}>Reason / Note *</label>
              <textarea
                value={note}
                onChange={e => { setError(""); setNote(e.target.value); }}
                rows={3}
                placeholder="Brief reason approved by all flat owners"
                style={{ ...inputStyle, resize: "vertical", lineHeight: "1.5", fontFamily: "inherit", padding: "10px 14px" }}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={labelStyle}>Signed Document Photo * (compressed and stored securely)</label>
              {photo ? (
                <div>
                  <img src={photo} alt="Signed doc" style={{
                    width: "100%", borderRadius: "10px", border: "1px solid #2e2e40",
                    marginBottom: "8px", maxHeight: "200px", objectFit: "cover",
                  }} />
                  <button onClick={() => setPhoto(null)} style={{
                    background: "#ff525210", border: "1px solid #ff525230", borderRadius: "8px",
                    padding: "6px 14px", fontSize: "12px", color: "#ff5252", cursor: "pointer",
                  }}>Remove Photo</button>
                </div>
              ) : (
                <div>
                  <input type="file" accept="image/*" id="exception-photo" style={{ display: "none" }} onChange={handlePhotoUpload} />
                  <button onClick={() => document.getElementById("exception-photo").click()} style={{
                    width: "100%", background: "#6c63ff10", border: "1px dashed #6c63ff40",
                    borderRadius: "10px", padding: "14px", fontSize: "13px", color: "#6c63ff", cursor: "pointer",
                  }}>
                    📷 Upload Signed Document Photo
                  </button>
                </div>
              )}
            </div>

            {error && <div style={{ background: "#ff525215", border: "1px solid #ff525240", borderRadius: "10px", padding: "10px 14px", fontSize: "13px", color: "#ff5252", marginBottom: "12px" }}>⚠ {error}</div>}

            {!showConfirm ? (
              <div style={{ display: "flex", gap: "8px" }}>
                <button onClick={() => { setShowForm(false); setError(""); setAmount(""); setNote(""); setPhoto(null); }} style={{
                  flex: 1, background: "transparent", color: "#8888a8",
                  border: "1px solid #2e2e40", borderRadius: "10px",
                  padding: "12px", fontSize: "13px", fontWeight: "600", cursor: "pointer",
                }}>Cancel</button>
                <button onClick={() => { if (validate()) setShowConfirm(true); }} style={{
                  flex: 2, background: "#ffa726", color: "#000", border: "none",
                  borderRadius: "10px", padding: "12px", fontSize: "14px", fontWeight: "700", cursor: "pointer",
                }}>Review & Confirm</button>
              </div>
            ) : (
              <div style={{
                background: "#ff525210", border: "1px solid #ff525240",
                borderRadius: "12px", padding: "14px", marginTop: "4px",
              }}>
                <div style={{ fontSize: "13px", fontWeight: "700", color: "#ff5252", marginBottom: "8px" }}>
                  ⚠ Final Confirmation
                </div>
                <div style={{ fontSize: "12px", color: "#8888a8", marginBottom: "4px" }}>
                  Flat {flat.flatNo} rate: ₹{Number(computeMonthlyRate(flat, settings)).toLocaleString("en-IN")} → ₹{Number(amount).toLocaleString("en-IN")} / month
                </div>
                <div style={{ fontSize: "12px", color: "#8888a8", marginBottom: "12px" }}>
                  All pending unpaid dues will be recalculated. This cannot be undone.
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button onClick={() => setShowConfirm(false)} style={{
                    flex: 1, background: "transparent", color: "#8888a8",
                    border: "1px solid #2e2e40", borderRadius: "10px",
                    padding: "11px", fontSize: "13px", fontWeight: "600", cursor: "pointer",
                  }}>Go Back</button>
                  <button onClick={handleApply} disabled={saving} style={{
                    flex: 2, background: "#ff5252", color: "#fff", border: "none",
                    borderRadius: "10px", padding: "11px", fontSize: "14px", fontWeight: "700",
                    cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1,
                  }}>{saving ? "Applying…" : "Apply Exception"}</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ── Exception already exists ──
  return (
    <div>
      {success && <div style={{ background: "#4caf5015", border: "1px solid #4caf5040", borderRadius: "10px", padding: "10px 14px", fontSize: "13px", color: "#4caf50", marginBottom: "12px" }}>{success}</div>}

      <div style={{
        background: "#6c63ff10", border: "1px solid #6c63ff30",
        borderRadius: "12px", padding: "16px", marginBottom: "14px",
      }}>
        <div style={{ fontSize: "11px", fontWeight: "600", color: "#6c63ff", marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
          ⚡ Active Exception
        </div>
        {[
          ["New Rate",     `₹${Number(exception.exceptionAmount).toLocaleString("en-IN")} / month`],
          ["Approved By",  exception.exceptionApprovedBy],
          ["Approved On",  exception.exceptionApprovedOn],
          ["Note",         exception.exceptionNote],
        ].map(([label, value]) => (
          <div key={label} style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
            <span style={{ fontSize: "12px", color: "#555570" }}>{label}</span>
            <span style={{ fontSize: "12px", color: "#8888a8", textAlign: "right", maxWidth: "60%" }}>{value}</span>
          </div>
        ))}
      </div>

      {exception.exceptionRecalc && (
        <div style={{
          background: "#0f0f13", border: "1px solid #2e2e40",
          borderRadius: "12px", padding: "14px", marginBottom: "14px",
        }}>
          <div style={{ fontSize: "11px", fontWeight: "600", color: "#8888a8", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Recalculation Summary
          </div>
          <div style={{ fontSize: "12px", color: "#8888a8", marginBottom: "4px" }}>
            Months affected: {exception.exceptionRecalc.months.length}
          </div>
          <div style={{ fontSize: "13px", color: "#4caf50", fontWeight: "600" }}>
            Total reduction: ₹{Number(exception.exceptionRecalc.totalReduction).toLocaleString("en-IN")}
          </div>
        </div>
      )}

      {exception.exceptionPhoto && (
        <div style={{ marginBottom: "14px" }}>
          <div style={{ fontSize: "12px", color: "#8888a8", marginBottom: "6px" }}>Signed Document</div>
          <img src={exception.exceptionPhoto} alt="Signed document"
            style={{ width: "100%", borderRadius: "10px", border: "1px solid #2e2e40", maxHeight: "220px", objectFit: "cover" }}
          />
        </div>
      )}

      <button onClick={downloadReport} style={{
        width: "100%", background: "#4caf5015", color: "#4caf50",
        border: "1px solid #4caf5040", borderRadius: "10px",
        padding: "13px", fontSize: "14px", fontWeight: "600", cursor: "pointer",
      }}>
        ⬇ Download Recalculation Report
      </button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN FLATS SCREEN
// ══════════════════════════════════════════════════════════════
export default function FlatsScreen() {
  const { user, isMC } = useAuth();
  const [flats,       setFlats]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [selected,    setSelected]    = useState(null);
  const [search,      setSearch]      = useState("");
  const [tenantModal, setTenantModal] = useState(null); // flat object for tenant modal
  const [hasTenantMap, setHasTenantMap] = useState({}); // flatNo → true/false
  const [settings, setSettings] = useState(null); // settings/init — maintenanceAmt, vacantPct

  const canEdit = isMC() && user?.role !== "Adyantam";

  useEffect(() => {
    async function load() {
      try {
        // Load Settings once, so every flat's rate can be worked out consistently
        const settingsSnap = await getDoc(doc(db, "settings", "init"));
        if (settingsSnap.exists()) setSettings(settingsSnap.data());

        const flatsQuery = query(
          collection(db, "flats"),
          where("societyId", "==", user?.societyId)
        );
        const snap = await getDocs(flatsQuery);
        const data = sortFlats(snap.docs.map(d => ({ flatNo: d.id, ...d.data() })));
        setFlats(data);

        // Load active tenants to know which flats have tenants
        const tSnap = await getDocs(query(
          collection(db, "users"),
          where("societyId", "==", user?.societyId),
          where("memberType", "==", "tenant"),
          where("active", "==", true)
        ));
        const map = {};
        tSnap.docs.forEach(d => { map[d.data().flatId] = true; });
        setHasTenantMap(map);
      } catch (e) { console.error("Failed to load flats:", e); }
      finally { setLoading(false); }
    }
    load();
  }, []);

  const visibleFlats = canEdit
    ? flats.filter(f =>
        search.trim() === "" ||
        f.flatNo.toLowerCase().includes(search.toLowerCase()) ||
        (f.ownerName || "").toLowerCase().includes(search.toLowerCase())
      )
    : flats.filter(f => userHasFlat(user, f.flatNo));

  function handleSave(updated) {
    setFlats(prev => sortFlats(prev.map(f => f.flatNo === updated.flatNo ? updated : f)));
  }

  if (loading) return (
    <div style={{ textAlign: "center", padding: "40px", color: "#555570", fontSize: "13px" }}>Loading flats…</div>
  );

  const grouped = {};
  if (canEdit) {
    for (const flat of visibleFlats) {
      const floor = flat.floor || "Other";
      if (!grouped[floor]) grouped[floor] = [];
      grouped[floor].push(flat);
    }
  }

  return (
    <div>
      <div style={{ marginBottom: "14px" }}>
        <div style={{ fontSize: "15px", fontWeight: "700", color: "#e8e8f0" }}>🏠 Flats</div>
        <div style={{ fontSize: "12px", color: "#555570", marginTop: "2px" }}>
          {canEdit ? `${flats.length} flats · tap to view or edit` : "Your flat details"}
        </div>
      </div>

      {canEdit && (
        <input placeholder="Search by flat no. or owner name…" value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            width: "100%", background: "#1a1a24", border: "1px solid #2e2e40",
            borderRadius: "10px", padding: "11px 14px", color: "#e8e8f0",
            fontSize: "14px", outline: "none", boxSizing: "border-box", marginBottom: "14px",
          }}
        />
      )}

      {canEdit && search === "" && (
        <div style={{ display: "flex", gap: "8px", marginBottom: "16px", flexWrap: "wrap" }}>
          {[
            { label: "Total",    value: flats.length,                                      color: "#6c63ff" },
            { label: "Occupied", value: flats.filter(f => f.status === "Occupied").length, color: "#4caf50" },
            { label: "Vacant",   value: flats.filter(f => f.status === "Vacant").length,   color: "#ffa726" },
          ].map(c => (
            <div key={c.label} style={{
              background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "10px",
              padding: "8px 14px", display: "flex", gap: "6px", alignItems: "center",
            }}>
              <span style={{ fontSize: "16px", fontWeight: "700", color: c.color }}>{c.value}</span>
              <span style={{ fontSize: "11px", color: "#555570" }}>{c.label}</span>
            </div>
          ))}
        </div>
      )}

      {canEdit && FLOOR_ORDER.map(floor => {
        const group = grouped[floor];
        if (!group || group.length === 0) return null;
        return (
          <div key={floor}>
            <div style={{
              fontSize: "11px", fontWeight: "600", color: "#8888a8", textTransform: "uppercase",
              letterSpacing: "0.5px", marginBottom: "8px", marginTop: "4px",
              paddingBottom: "6px", borderBottom: "1px solid #2e2e40",
            }}>{floor}</div>
            {group.map(flat => (
              <FlatCard
                key={flat.flatNo}
                flat={flat}
                settings={settings}
                onClick={() => setSelected(flat)}
                showTenantBtn={userHasFlat(user, flat.flatNo)}
                hasTenant={!!hasTenantMap[flat.flatNo]}
                onTenantClick={() => setTenantModal(flat)}
              />
            ))}
          </div>
        );
      })}

      {!canEdit && visibleFlats.length === 0 && (
        <div style={{ textAlign: "center", padding: "40px", color: "#555570", fontSize: "13px" }}>
          No flat assigned to your account.
        </div>
      )}
      {!canEdit && visibleFlats.map(flat => (
        <FlatCard
          key={flat.flatNo}
          flat={flat}
          settings={settings}
          onClick={() => setSelected(flat)}
          showTenantBtn={true}
          hasTenant={!!hasTenantMap[flat.flatNo]}
          onTenantClick={() => setTenantModal(flat)}
        />
      ))}

      {selected && (
        <FlatDetail
          flat={selected} canEdit={canEdit} currentUser={user} settings={settings}
          onClose={() => setSelected(null)}
          onSave={updated => { handleSave(updated); setSelected(updated); }}
        />
      )}

      {/* Tenant modal — opened directly from card button */}
      {tenantModal && (
        <div style={{
          position: "fixed", inset: 0, background: "#000000cc", zIndex: 200,
          display: "flex", alignItems: "flex-end", justifyContent: "center",
        }} onClick={e => { if (e.target === e.currentTarget) setTenantModal(null); }}>
          <div style={{
            background: "#1a1a24", borderRadius: "20px 20px 0 0", border: "1px solid #2e2e40",
            width: "100%", maxWidth: "480px", padding: "20px", maxHeight: "90vh", overflowY: "auto",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <div>
                <div style={{ fontSize: "16px", fontWeight: "700", color: "#e8e8f0" }}>
                  Flat {tenantModal.flatNo} — Tenant
                </div>
                <div style={{ fontSize: "11px", color: "#555570", marginTop: "2px" }}>
                  Manage tenant for your flat
                </div>
              </div>
              <button onClick={() => setTenantModal(null)} style={{
                background: "#2e2e40", border: "none", borderRadius: "8px",
                color: "#8888a8", fontSize: "13px", padding: "6px 12px", cursor: "pointer",
              }}>Close</button>
            </div>
            <ManageTenant
              flat={tenantModal}
              currentUser={user}
              onClose={() => {
                // Refresh tenant map after add/exit
                getDocs(query(collection(db, "users"), where("societyId","==",user?.societyId), where("memberType","==","tenant"), where("active","==",true)))
                  .then(snap => {
                    const map = {};
                    snap.docs.forEach(d => { map[d.data().flatId] = true; });
                    setHasTenantMap(map);
                  });
                setTenantModal(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
